import {
  useMemo,
  useState,
  useCallback,
  useEffect,
  useLayoutEffect,
  type RefObject,
} from "react";

const GAP_LG = 24;
const GAP_SM = 16;

export function getColumnCount(width: number): number {
  if (width <= 480) return 1;
  if (width <= 768) return 2;
  return 3;
}

export function getMasonryGap(width: number): number {
  return width <= 768 ? GAP_SM : GAP_LG;
}

function distributeByShortestColumn<T>(
  items: T[],
  columnCount: number,
  getItemHeight: (item: T) => number,
  gap: number,
): T[][] {
  const columns = Array.from({ length: columnCount }, () => [] as T[]);
  const heights = Array(columnCount).fill(0);

  for (const item of items) {
    let shortest = 0;
    for (let i = 1; i < columnCount; i++) {
      if (heights[i] < heights[shortest]) shortest = i;
    }
    columns[shortest].push(item);
    heights[shortest] += getItemHeight(item) + gap;
  }

  return columns;
}

export function useMasonryColumns<T extends { id: string }>(
  items: T[],
  containerRef: RefObject<HTMLElement | null>,
) {
  const [containerWidth, setContainerWidth] = useState(0);
  const [dimensions, setDimensions] = useState<
    Record<string, { width: number; height: number }>
  >({});

  useLayoutEffect(() => {
    const el = containerRef.current;
    if (el) setContainerWidth(el.getBoundingClientRect().width);
  }, [containerRef]);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    const update = () => setContainerWidth(el.getBoundingClientRect().width);
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, [containerRef]);

  const columnCount = getColumnCount(containerWidth);
  const gap = getMasonryGap(containerWidth);
  const columnWidth =
    containerWidth > 0
      ? (containerWidth - gap * (columnCount - 1)) / columnCount
      : 0;

  const columns = useMemo(() => {
    const getHeight = (item: T) => {
      const dim = dimensions[item.id];
      if (dim && dim.width > 0) {
        return (columnWidth * dim.height) / dim.width;
      }
      return columnWidth > 0 ? columnWidth * 0.75 : 200;
    };

    return distributeByShortestColumn(items, columnCount, getHeight, gap);
  }, [items, columnCount, columnWidth, gap, dimensions]);

  const registerImageDimensions = useCallback(
    (id: string, naturalWidth: number, naturalHeight: number) => {
      setDimensions((prev) => {
        const existing = prev[id];
        if (
          existing?.width === naturalWidth &&
          existing?.height === naturalHeight
        ) {
          return prev;
        }
        return { ...prev, [id]: { width: naturalWidth, height: naturalHeight } };
      });
    },
    [],
  );

  return { columns, registerImageDimensions, gap };
}
